import { Model } from 'dva'
import { Flow } from './types'
import { OrganisationsService } from '../../Service'
import { IsObject } from '../../Utils/helpers'
import { FailureHandler } from 'Utils/ApiHelper'
import { message } from 'antd'

const Flows: Model = {
  namespace: 'flows',
  state: {
    list: [],
    flow: null,
  },
  effects: {
    *list(
      {
        useCache = true,
        overwriteList = false,
        onlyTemplates = false,
        setLoading = () => {},
        onSuccess = () => {},
        onFailure = () => {},
        additionalParams = {},
      },
      { call, put, select }
    ): any {
      setLoading(true)
      try {
        let flows = yield select((state: any) => state.flows.list)
        if (onlyTemplates) {
          flows = flows.filter((flow: Flow) => flow.is_template)
        }
        if (!(flows.length > 0 && useCache)) {
          flows = yield call(OrganisationsService.organisationsFlowsList, {
            is_template: onlyTemplates ? onlyTemplates : undefined,
            ordering: ['name'],
            ...additionalParams,
          })
          if (overwriteList) {
            yield put({ type: 'setList', payload: flows })
          } else {
            yield put({ type: 'modifyList', payload: flows })
          }
        }
        onSuccess(flows)
      } catch (err: any) {
        FailureHandler('Error fetching flows', err, onFailure)
      }
      setLoading(false)
    },
    *listGlobalTemplates(
      {
        useCache = true,
        overwriteList = false,
        onlyTemplates = false,
        setLoading = () => {},
        onSuccess = () => {},
        onFailure = () => {},
        additionalParams = {},
      },
      { call, put, select }
    ): any {
      setLoading(true)
      try {
        let flows = yield select((state: any) => state.flows.list)
        if (onlyTemplates) {
          flows = flows.filter((flow: Flow) => flow.is_template)
        }
        if (!(flows.length > 0 && useCache)) {
          flows = yield call(OrganisationsService.organisationsFlowsGlobalTemplatesRetrieve, {
            organisationUuid: additionalParams?.organisation_uuid,
          })
          if (overwriteList) {
            yield put({ type: 'setList', payload: flows })
          } else {
            yield put({ type: 'modifyList', payload: flows })
          }
        }
        onSuccess(flows)
      } catch (err: any) {
        FailureHandler('Error fetching flows', err, onFailure)
      }
      setLoading(false)
    },
    // flows/retrieve not in use currently
    *retrieve(
      { uuid, organisationUuid, setLoading = () => {}, onSuccess = () => {}, onFailure = () => {} },
      { call, put, select }
    ): any {
      setLoading(true)
      try {
        let flow = yield select((state: any) =>
          state.flows.list.find((flow: Flow) => flow.uuid === uuid)
        )
        if (!flow) {
          flow = yield call(OrganisationsService.organisationsFlowsRetrieve, {
            uuid: uuid,
            organisationUuid,
          })
          yield put({ type: 'setState', payload: { flow } })
        } else {
          yield put({ type: 'setState', payload: { flow } })
        }
        yield put({ type: 'modifyList', payload: flow })
        onSuccess(flow)
      } catch (err) {
        FailureHandler('Could not find the flow!', err, onFailure)
      }
      setLoading(false)
    },
    *create(
      {
        data = {},
        setLoading = () => {},
        onSuccess = () => {},
        onFailure = () => {},
        organisationUuid = null,
      },
      { call, put }
    ): any {
      setLoading(true)
      try {
        const response = yield call(OrganisationsService.organisationsFlowsCreate, {
          requestBody: data,
          organisationUuid,
        })
        yield put({ type: 'modifyList', payload: response })
        onSuccess(response)
      } catch (err) {
        FailureHandler('Error adding a flow', err, onFailure)
      }
      setLoading(false)
    },
    *createGlobalTemplate(
      {
        data = {},
        setLoading = () => {},
        onSuccess = () => {},
        onFailure = () => {},
        organisationUuid = null,
      },
      { call, put }
    ): any {
      // Note: currently this effect is not in use
      // Please update when Global Templates are implemented
      setLoading(true)
      try {
        const response = yield call(
          OrganisationsService.organisationsFlowsCreateGlobalTemplateCreate,
          {
            organisationUuid,
            requestBody: data,
          }
        )
        yield put({ type: 'modifyList', payload: response })
        onSuccess(response)
      } catch (err) {
        FailureHandler('Error adding a flow', err, onFailure)
      }
      setLoading(false)
    },
    *partialUpdate(
      { uuid, data, setLoading = () => {}, onSuccess = () => {}, onFailure = () => {} },
      { call, put, select }
    ): any {
      const organisationUuid = yield select(
        (state: any) => state.flowLayout.flow?.organisation?.uuid
      )
      setLoading(true)
      try {
        const response = yield call(OrganisationsService.organisationsFlowsPartialUpdate, {
          organisationUuid,
          uuid: uuid,
          requestBody: data,
        })
        yield put({ type: 'modifyList', payload: response })
        onSuccess(response)
      } catch (err) {
        FailureHandler('Error editing a flow', err, onFailure)
      }
      setLoading(false)
    },
    *destroy(
      { uuid, setLoading = () => {}, onSuccess = () => {}, onFailure = () => {} },
      { call, put, select }
    ): any {
      setLoading(true)
      try {
        yield call(OrganisationsService.organisationsFlowsDestroy, { uuid: uuid })
        yield put({ type: 'modifyList', payload: uuid })
        onSuccess()
      } catch (err) {
        FailureHandler('Error deleting a flow', err, onFailure)
      }
      setLoading(false)
    },
    *convertToSubFlow(
      { payload, onSuccess = () => {}, onFailure = () => {} },
      { call, put, select }
    ): any {
      const flowLayout = yield select((state: any) => state.flowLayout)
      try {
        const response = yield call(
          OrganisationsService.organisationsFlowsVersionsSubFlowsConvertToSubflowCreate,
          {
            flowUuid: flowLayout.flow.uuid,
            organisationUuid: flowLayout.flow?.organisation?.uuid,
            flowConfigVersion: flowLayout.version.version,
            requestBody: {
              ...payload,
              current_subflow_id: flowLayout.subFlow.id,
            },
          }
        )
        onSuccess(response)
      } catch (err) {
        message.error('Error converting to subflow!')
        onFailure()
      }
    },
    *reverseConvertToSubFlow({ payload, onSuccess = () => {} }, { call, put, select }): any {
      const flowLayout = yield select((state: any) => state.flowLayout)
      try {
        const response = yield call(
          OrganisationsService.organisationsFlowsVersionsSubFlowsReverseConvertToSubflowCreate,
          {
            organisationUuid: flowLayout.flow?.organisation?.uuid,
            flowUuid: flowLayout.flow.uuid,
            flowConfigVersion: flowLayout.version.version,
            requestBody: {
              ...payload,
            },
          }
        )
        onSuccess(response)
      } catch (err) {
        message.error('Error reverting the subflow!')
      }
    },
  },
  reducers: {
    setState(state, action) {
      return {
        ...state,
        ...action.payload,
      }
    },
    setList(state, action) {
      if (Array.isArray(action?.payload)) {
        return {
          state,
          list: action.payload,
        }
      }
      return state
    },
    modifyList(state, action) {
      // clone the list
      let list = [...state.list]
      if (IsObject(action.payload)) {
        // if the payload is an object, it's a single object, so add it to the list
        list = list.filter((l: Flow) => l.uuid !== action.payload.uuid)
        list.push(action.payload)
      } else if (Array.isArray(action.payload)) {
        // if the payload is an array, it's a list of objects, so add them to the list
        list = list
          .filter((l: Flow) => !action.payload.map((p: Flow) => p.uuid).includes(l.uuid))
          .concat(action.payload)
      } else if (Number.isInteger(action.payload)) {
        // if the payload is an integer, it's an id sent in delete operations, so remove it from the list
        list = list.filter((l: Flow) => l.uuid !== action.payload)
      }
      return {
        ...state,
        list: list,
      }
    },
    resetFlows(state, action) {
      return {
        ...state,
        list: [],
      }
    },
  },
}

export default Flows
